import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/platform/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null); // { workspace, role, subscription }
  const [loading, setLoading] = useState(true);

  // Capture whether this initial load is an OAuth callback SYNCHRONOUSLY at
  // first render \u2014 before AuthCallback (a child, whose effects run first) can
  // strip the hash. Reading it inside the effect below is too late and causes
  // a /me race that clobbers the user set by AuthCallback (bounce to /login).
  const isOAuthCallback = useRef(
    typeof window !== "undefined" && (window.location.hash || "").includes("session_id=")
  );

  const applyAuth = useCallback((payload) => {
    if (!payload) return;
    setUser(payload.user || null);
    setWorkspace(payload.workspace || null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const d = await api.get("/api/auth/me");
      setUser(d.user || null);
      setWorkspace(d.workspace || null);
      return d;
    } catch (_e) {
      setUser(null);
      setWorkspace(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from the Google OAuth callback, skip the /me
    // check \u2014 AuthCallback exchanges the session_id and sets the cookie first.
    // Uses the ref captured at first render so effect ordering can't defeat it.
    if (isOAuthCallback.current) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    setUser(null);
    setWorkspace(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, workspace, loading, applyAuth, refresh, setWorkspace, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
